import {BehaviorSubject, combineLatest, Subject} from 'rxjs';
import {debounceTime, filter, skipUntil, switchMap} from 'rxjs/operators';
import {numUtil, timeUtil} from '@/helper';
import {mc} from '@/core';
import {referralApi} from '@/api/referral';
import {activityApi} from '@/api/activity';
import {browser$} from '@/streams/browser';
import {userStatus$} from '@/streams/auth';
import {CHANNEL, REFERRAL_PAGE_ZIE, SCENE} from './const';

export const inviteDialogVisible$ = new BehaviorSubject(false);
export const loading$ = new BehaviorSubject(false);
export const loadReferralList$ = new Subject();
export const referralList$ = new BehaviorSubject(createInitData());

export const checkAccessible$ = new Subject();
export const checkUserIsTargetInvitee$ = new Subject();
export const stagesInfo$ = new BehaviorSubject([]);
export const myOverview$ = new BehaviorSubject({
  totalCount: 0,
  mintCount: 0,
  rewards: 0,
  rewardsToGet: 0,
});
export const selectedStage$ = new BehaviorSubject('');
export const myRewards$ = new BehaviorSubject({
  rewardAsInviter: 0,
  rewardAsInvitee: 0,
});
export const isTargetInvitee$ = new BehaviorSubject(false);
export const referralDetails$ = new BehaviorSubject([]);
export const referralTotal$ = new BehaviorSubject([]);
export const referralPageIndex$ = new BehaviorSubject(0);

export const loadingReferral$ = new BehaviorSubject(false);
export const loadReferralReward$ = new Subject();
export const referralRewardList$ = new BehaviorSubject(createInitData());

export const loadReferralStageRewards$ = new Subject();
export const loadReferralSummary$ = new Subject();
export const referralRewardSummary$ = new BehaviorSubject({
  referralCount: 0,
  bonusCount: 0,
  voucherCount: 0,
  recommendUsdtAmountEv: 0,
  selfAwardUsdtAmountEv: 0,
});
export const newReferralAccessible$ = new BehaviorSubject(true);

export const loadReferralConfig$ = new Subject();
export const newReferralConfig$ = new BehaviorSubject(genInitConfig());

export const loadReferralEarning$ = new Subject();
export const newReferralEarningInfo$ = new BehaviorSubject(genInitData());
export const loadReferralHistory$ = new Subject();
export const newReferralHistory$ = new BehaviorSubject(createInitData());

loadReferralReward$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadReferralReward(query))
  )
  .subscribe((x) => referralRewardList$.next(x));

loadReferralList$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadReferralLink(query))
  )
  .subscribe((x) => referralList$.next(x));

loadReferralSummary$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadReferralRewardSummary(query))
  )
  .subscribe((x) => referralRewardSummary$.next(x));

checkAccessible$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => activityApi.getActivityGrayInfo(CHANNEL, SCENE))
  )
  .subscribe(({data}) => {
    newReferralAccessible$.next(data.access);
  });

checkUserIsTargetInvitee$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => activityApi.getUserReferralType(CHANNEL, SCENE))
  )
  .subscribe(({data}) => {
    isTargetInvitee$.next(data.statusType && data.statusType === 'C2C');
    isTargetInvitee$.next(true);
  });

loadReferralStageRewards$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => activityApi.getReferralStageRewards(CHANNEL, SCENE))
  )
  .subscribe(({data}) => {
    const stages = {};
    data.rows.forEach((item) => {
      stages[item.stageKey] = stages[item.stageKey] || [];
      stages[item.stageKey].push({
        inviteePreMineCount: item.limitAwardRv,
        referralAwardCount: item.referralAwardRv,
        inviteeAwardCount: item.inviteeAwardRv,
      });
    });
    const ids = Object.keys(stages).sort();
    const stageInfos = ids.map((id) => {
      return {
        round: id,
        levels: stages[id],
        stageId: id.match(/\d+/)[0] * 1,
      };
    });
    stagesInfo$.next(stageInfos);
  });

// referral 基本配置
loadReferralConfig$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => referralApi.getReferralConfig({}))
  )
  .subscribe(({data}) => {
    if (data) {
      const config = {
        startTimeTs: data.startTimeTs,
        inviterPrize: numUtil.delimit(data.inviterPrize),
        inviterCommission: numUtil.delimit(data.inviterCommission),
        inviteeRewards: numUtil.delimit(data.inviteeRewards),
        earnTrade: numUtil.delimit(data.earnTrade),
      };
      newReferralConfig$.next(config);
    }
  });

// earning dashboard
loadReferralEarning$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => referralApi.getReferralEarning({}))
  )
  .subscribe(({data}) => {
    if (data) {
      newReferralEarningInfo$.next({currentMon: convertEarningDashboard(data.currentMon), allMon: convertEarningDashboard(data.allMon)});
    }
  });

// referral history
loadReferralHistory$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => referralApi.getReferralHistory(query))
  )
  .subscribe(({data, fail}) => {
    if (!fail) {
      newReferralHistory$.next(convertReferralHistory(data));
    }
  });

combineLatest([userStatus$, loadReferralStageRewards$])
  .pipe(filter(([status]) => status === 'Login'))
  .subscribe(async () => {
    const {data} = await activityApi.getReferralTotalInfo(CHANNEL, SCENE);
    if (data) {
      myOverview$.next({
        totalCount: data.inviteeNums,
        mintCount: data.inviteeMintedNums,
        rewards: data.totalAwardsRv,
        rewardsToGet: data.unReceivedAwardsRv,
      });
    }
  });

combineLatest([selectedStage$, userStatus$, stagesInfo$, referralPageIndex$])
  .pipe(
    filter(([selectedStage, status, stagesInfo, pageIndex]) => selectedStage && status === 'Login' && stagesInfo.length && pageIndex),
    debounceTime(100)
  )
  .subscribe(async ([selectedStage, , stagesInfo, pageIndex]) => {
    const stageKey = stagesInfo.find((item) => item.stageId === selectedStage).round;
    const {data} = await activityApi.getReferralInfo({stageKey, channel: CHANNEL, scene: SCENE, index: pageIndex, size: REFERRAL_PAGE_ZIE});
    const stageData = data.mindAwardsDetailV0List ? data.mindAwardsDetailV0List : [];
    myRewards$.next({
      rewardAsInviter: data.referAwardRv,
      rewardAsInvitee: data.selfAwardRv,
    });
    referralTotal$.next(data.totalInviteeNums);
    referralDetails$.next(
      stageData.map((item) => {
        return {
          email: item.email,
          registerTime: item.registrationTime,
          mintTime: item.mintTime,
          minedXPT: item.mindAwardRv,
          rewardXPT: item.referAward,
        };
      })
    );
  });

async function loadReferralLink(query) {
  loading$.next(true);
  const {data, error} = await referralApi.getReferralLink(query);
  loading$.next(false);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return createInitData();
}

async function loadReferralReward(query) {
  loadingReferral$.next(true);
  const {data, error} = await referralApi.getReferralReward(query);
  loadingReferral$.next(false);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return createInitData();
}

async function loadReferralRewardSummary(query) {
  const {data, error} = await referralApi.getReferralRewardSummary(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {
    referralUserCount: 0,
    bonusCount: 0,
    voucherCount: 0,
    recommendUsdtAmountEv: 0,
    selfAwardUsdtAmountEv: 0,
  };
}

function createInitData() {
  return {total: 0, rows: []};
}

function genInitData() {
  return {
    currentMon: {
      total: '0',
      qualified: '0',
      poolRewardEv: '0',
      commissionEv: '0',
    },
    allMon: {
      total: '0',
      qualified: '0',
      poolRewardEv: '0',
      commissionEv: '0',
    },
  };
}

function genInitConfig() {
  return {
    startTimeTs: 0,
    inviterPrize: '0',
    inviterCommission: '0',
    inviteeRewards: '0',
    earnTrade: '0',
  };
}

function convertReferralHistory(data) {
  return {
    total: data.total,
    rows: data.rows.map((r) => {
      return {
        registrationTime: timeUtil.formatUTCDateTime(r.createTime),
        userId: r.userId,
        advanceKycTime: r.finishKycTime ? timeUtil.formatUTCDateTime(r.finishKycTime) : '-',
        tradingVolume: numUtil.delimit(numUtil.floor(r.tradeUsdtAmountEv, 2, 8)),
        qualifiedReferee: r.finishStatus ? 'Yes' : 'No',
        commission: r.commissionAmountEv ? numUtil.delimit(numUtil.floor(r.commissionAmountEv, 2, 8)) : '-',
      };
    }),
  };
}

function convertEarningDashboard(data) {
  return {
    total: numUtil.delimit(data.total),
    qualified: numUtil.delimit(data.qualified),
    poolRewardEv: data.poolRewardEv ? numUtil.delimit(numUtil.floor(data.poolRewardEv, 2, 8)) : '',
    commissionEv: data.commissionEv ? numUtil.delimit(numUtil.floor(data.commissionEv, 2, 8)) : '',
  };
}
